/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-17 17:57:36
 *
 *******************************************************/
// @flow
import React from 'react';
import { DataTable, Button, Icon } from '@shopify/polaris';
import { change, Field, getFormValues } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { DIRECTION } from 'src/constants/synonym';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import TableHeadingCellWithTooltip from 'src/components/views/synonym/table-heading-cell-with-tooltip';
import DirectionTooltip from './direction-tooltip';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import SuggestionTooltip from './suggestion-tooltip';
import { DeleteMinor } from '@shopify/polaris-icons';
import _ from 'lodash';
import { connect } from 'react-redux';

type Props = {
  skip: number,
  limit: number,
  discardDeletingChange: boolean,
  handleChangeDeletingButtonGroup: () => void,
  turnOffDiscardDeletingChangeFlag: () => void
};

type ComposedProps = Props & WithI18nProps;

function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const SynonymsEdit = (props: ComposedProps) => {
  const {
    i18n,
    fields,
    deletedSynonyms,
    addDeletedSynonyms,
    addUpdatedSynonyms,
    loading
  } = props;
  const prevAllField = usePrevious(props.fields.getAll()) || [];

  const isSynonymDiff = (s1, s2) => {
    return (
      s1.enabled !== s2.enabled ||
      s1.searchTerm !== s2.searchTerm ||
      s1.synonyms !== s2.synonyms ||
      s1.allowSuggestion !== s2.allowSuggestion
    )
  }

  React.useEffect(() => {
    const allField = fields.getAll() || [];
    if (prevAllField.length === allField.length) {
      allField.forEach((field, index) => {
        if (isSynonymDiff(field, prevAllField[index])) {
          addUpdatedSynonyms(field);
        }
      })
    }
    // eslint-disable-next-line
  }, [fields])

  React.useEffect(() => {
    const { fields, updatedSynonyms, updateField } = props;
    if (!loading) {
      const allField = fields.getAll() || [];
      allField.forEach((f, index) => {
        if (updatedSynonyms[f.id]) {
          allField[index] = updatedSynonyms[f.id];
        }
      });
      updateField('synonyms', allField)
    }
    // eslint-disable-next-line
  }, [loading])

  // Render rows for data table
  const buildRows = () => {
    const allFields = fields.getAll();
    const rows = allFields.map((field, index) => {
      const synonym = `synonyms[${index}]`
      if (deletedSynonyms.includes(field.id)) {
        return null;
      }
      return [
        <Field name={`${synonym}.enabled`} component={RenderToggleSwitch} />,
        <Field
          name={`${synonym}.searchTerm`}
          component={RenderTextField}
          placeholder={i18n.translate('AdminHTML.Synonym.Synonym.searchTerm_Placeholder')}
        />,
        <Field name={`${synonym}.biDirection`} component={RenderSelect} options={DIRECTION} />,
        <Field
          name={`${synonym}.synonyms`}
          component={RenderTextField}
          placeholder={i18n.translate('AdminHTML.Synonym.Synonym.synonyms_Placeholder')}
        />,
        <Field name={`${synonym}.allowSuggestion`} component={RenderCheckbox} />,
        <Button
          onClick={() => addDeletedSynonyms(field.id)}>
          <Icon source={DeleteMinor} />
        </Button>
      ];
    });

    return rows.filter(r => r);
  };

  return (
    <DataTable
      verticalAlign="middle"
      columnContentTypes={['text', 'text', 'text']}
      headings={[
        i18n.translate('AdminHTML.Synonym.Synonym.status'),
        i18n.translate('AdminHTML.Synonym.Synonym.searchTerm'),
        <TableHeadingCellWithTooltip
          heading={i18n.translate('AdminHTML.Synonym.Synonym.direction')}
          tooltipContent={<DirectionTooltip i18n={i18n} />}
        />,
        <TableHeadingCellWithTooltip
          heading={i18n.translate('AdminHTML.Synonym.Synonym.synonyms')}
          tooltipContent={i18n.translate('AdminHTML.Synonym.Synonym.synonyms_Tooltip')}
        />,
        <TableHeadingCellWithTooltip
          heading={i18n.translate('AdminHTML.Synonym.Synonym.suggestionList')}
          tooltipContent={<SuggestionTooltip i18n={i18n} />}
        />,
        i18n.translate('AdminHTML.Synonym.Synonym.delete')
      ]}
      rows={buildRows()}
    />
  );
}
const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`EditSynonymsForm`, field, value)),
});
const mapStateToProps = (state, props) => {
  const formValues = getFormValues('EditSynonymsForm')(state);

  return {
    loading: _.get(state.synonym, 'loading', false),
    formValues
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SynonymsEdit))
